import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Icon from '../Icon/Icon';
import './BottomNavBar.scss';
import CircularProgress from '../CircularProgress/CircularProgress';
import { cleanError } from '../../Reducers/actions/uploadActions';
import { useSoftwall } from '../../containers/SoftWall/SoftWall';

const tabs = [
  {
    route: '/explore',
    icon: 'explore_inactive',
    isExplore: true
  },
  {
    route: '/news',
    icon: 'news_nav_v2',
    activeColor: '#FF3232',
    inactiveColor: '#000000'
  },
  {
    action: 'openCreateModal',
    icon: 'plusCircle_v2',
    width: 40,
    height: 40,
    activeColor: '#FF3232',
    inactiveColor: '#FF3232',
    isCreateButton: true,
    isCreate: true
  },
  {
    route: '/library',
    icon: 'library_active',
    inactive_icon: 'library_v2',
    activeColor: '#FF3232',
    inactiveColor: '#000000'
  },
  {
    route: '/profile',
    icon: 'person_active',
    inactive_icon: 'person',
    activeColor: '#FF3232',
    inactiveColor: '#000000',
    isProfile: true
  },
];

const BottomNavBar = ({ onOpenCreateModal }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { uploadLoading, uploadSuccess, uploadError, uploadProgress } = useSelector((state) => state.uploadState)
  const sw = useSoftwall();
  const requireAuth = sw?.requireAuth ?? (() => true);

  useEffect(() => {
    if (uploadSuccess) {
      setTimeout(() => {
        dispatch(cleanError());
      }, 3000);
    }
  }, [uploadSuccess, dispatch]);

  // Check if current route is active
  const isActive = (route) => {
    if (route === '/explore') {
      // Explore is only active if we're actually on explore
      return location.pathname.startsWith('/explore');
    }
    if (route === '/news') {
      // News is active if we're on /news OR /search/news
      return location.pathname.startsWith('/news') || location.pathname.startsWith('/search/news');
    }
    return location.pathname.startsWith(route);
  };

  const handleClick = (tab) => {
    if (tab.action === 'openCreateModal') {
      onOpenCreateModal();
    } else if (tab.route) {
      // Check if route requires authentication (library or profile)
      if (tab.route === '/library' || tab.route === '/profile') {
        const title = tab.route === '/library' 
          ? 'Sign up or log in to access your library' 
          : 'Sign up or log in to access your profile';
        if (!requireAuth(tab.route === '/library' ? 'Library Access' : 'Profile Access', title)) {
          return; // Softwall will be shown, don't navigate
        }
      }
      navigate(tab.route);
    }
  };

  const renderExploreIcon = (active) => (
    <div className="state-layer">
      <Icon
        alias="explore_inactive"
        width={24}
        height={24}
        color={active ? '#FF3232' : '#000000'}
      />
    </div>
  );

  const renderNewsIcon = (active) => (
    <div className="state-layer">
      <Icon
        alias="news_nav_v2"
        width={24}
        height={24}
        color={active ? '#FF3232' : '#000000'}
      />
    </div>
  );

  const renderCreateIcon = () => (
    <div className="create-icon-container">
      {uploadLoading ? <CircularProgress size={40} progress={uploadProgress} error={uploadError} />
        : uploadSuccess ?
          <CircularProgress size={40} progress={100} error={uploadError} />
          :
          <Icon
            alias="plusCircle_v2"
            width={40}
            height={40}
            color="#FF3232"
          />
      }
    </div>
  );

  const renderLibraryIcon = (active) => (
    <div className="state-layer">
      <Icon
        alias={active ? 'library_active' : 'library_v2'}
        width={24}
        height={24}
        color={active ? '#FF3232' : '#000000'}
      />
    </div>
  );

  const renderProfileIcon = (active) => (
    <div className="profile-icon-border">
      <div
        className="profile-picture-border"
        style={{
          backgroundImage: `url('${user?.profilePicture}')`,
          height: "24px",
          width: "24px"
        }}
      >
        {!user?.profilePicture && <Icon alias="profile-picture" height={24} width={24} />
        }<div
          className="profile-border-border"
          style={{ borderColor: active ? '#FF3232' : '#000000' }}
        />
      </div>

    </div>
  );

  return (
    <nav className="bottom-nav-bar">

      {tabs.map(tab => {
        const active = tab.route ? isActive(tab.route) : false;
        return (
          <button
            key={tab.route || tab.action}
            className={`nav-button ${active ? 'active' : ''} ${tab.isCreateButton ? 'create-button' : ''} ${tab.isProfile ? 'profile-button' : ''}`}
            onClick={() => handleClick(tab)}
          >
            <div className="icon-container">
              {tab.isExplore && renderExploreIcon(active)}
              {tab.route === '/news' && renderNewsIcon(active)}
              {tab.isCreateButton && renderCreateIcon()}
              {tab.route === '/library' && renderLibraryIcon(active)}
              {tab.isProfile && renderProfileIcon(active)}
            </div>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomNavBar; 